import { useState } from "react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog"
import type { AdminUser } from "@/api/hooks/admin"

interface ChangeRoleModalProps {
  user: AdminUser | null
  onClose: () => void
  onSubmit: (userId: number, newRole: string) => void
  isPending: boolean
}

export function ChangeRoleModal({ user, onClose, onSubmit, isPending }: ChangeRoleModalProps) {
  const [newRole, setNewRole] = useState(user?.role ?? "mediabuyer")

  // Preselect the user's current role whenever a different user is opened
  const [prevUser, setPrevUser] = useState(user)
  if (user !== prevUser) {
    setPrevUser(user)
    if (user) setNewRole(user.role)
  }

  return (
    <Dialog open={user !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="border-[#4a4a4a] bg-[#3a3a3a] text-white">
        <DialogHeader>
          <DialogTitle>Change Role</DialogTitle>
          <DialogDescription className="text-white">
            Change the role for {user?.username}.
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault()
            if (!user) return
            if (!window.confirm(`Are you sure you want to change this user's role to ${newRole}?`)) return
            onSubmit(user.id, newRole)
          }}
          className="space-y-4"
        >
          <div>
            <label className="mb-1 block text-sm font-medium">Current Role</label>
            <input type="text" readOnly
              value={user?.role ?? ""}
              className="w-full cursor-not-allowed rounded-md border border-[#555] bg-[#2d2d2d] px-3 py-2 text-white" />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium">New Role</label>
            <select required value={newRole} onChange={(e) => setNewRole(e.target.value)}
              className="w-full rounded-md border border-[#555] bg-[#2d2d2d] px-3 py-2 text-white">
              <option value="mediabuyer">Mediabuyer</option>
              <option value="team_leader">Team Leader</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="submit" disabled={isPending}
              className="flex-1 rounded-md border border-[#8fa0ef]/35 bg-[#7488e8] px-4 py-2 font-semibold text-white shadow-sm transition-colors hover:bg-[#687bda] disabled:opacity-50">
              Change Role
            </button>
            <button type="button" onClick={onClose}
              className="flex-1 rounded-md border border-[#7a808b]/45 bg-[#636b78] px-4 py-2 font-semibold text-white shadow-sm transition-colors hover:bg-[#58606c]">
              Cancel
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
